import { useId } from "react";
import { ArchiveBoxIcon, DocumentTextIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import { FieldRequirement } from "../components/FieldRequirement";
import { useLocale } from "../i18n";
import { validate } from "./validation";
import type { FieldValidation } from "./Fields";
import type { RecordValue, Schema } from "./types";

export function UnitLifecycleFields({ schema, value, onChange, validation }: {
  schema: Schema; value: RecordValue; onChange: (value: RecordValue) => void; validation: FieldValidation;
}) {
  const { w } = useLocale();
  const id = useId();
  const invalid = (key: string) => Boolean(validation.attempt && validate(schema.properties![key], value[key], Boolean(schema.required?.includes(key))).length);
  const states = [
    { value: "DRAFT", title: w("مسودة", "Draft"), description: w("حفظ الحالة كمسودة غير منشورة.", "Keep this season as an unpublished draft."), Icon: DocumentTextIcon },
    { value: "PUBLISHED", title: w("منشورة", "Published"), description: w("نشر الموسم بعد استكمال الإعدادات المطلوبة.", "Publish after completing the required setup."), Icon: GlobeAltIcon },
    { value: "ARCHIVED", title: w("مؤرشفة", "Archived"), description: w("نقل الموسم إلى الأرشيف مع الاحتفاظ ببياناته.", "Move this season to the archive and retain its data."), Icon: ArchiveBoxIcon },
  ];
  return <div className="event-lifecycle-fields">
    <fieldset className="event-lifecycle-status" data-field-path="status" aria-describedby={invalid("status") ? `${id}-status-error` : undefined} aria-invalid={invalid("status")}>
      <legend>{w("الحالة الجديدة", "New status")} <FieldRequirement required /></legend>
      <div className="event-lifecycle-choices">{states.filter(state => schema.properties?.status.enum?.includes(state.value)).map(({ value: status, title, description, Icon }) =>
        <label key={status} className={value.status === status ? "is-selected" : undefined}>
          <input type="radio" name={`${id}-status`} value={status} checked={value.status === status} required onChange={() => onChange({ ...value, status })} />
          <Icon aria-hidden="true" /><span><strong>{title}</strong><small>{description}</small></span>
        </label>)}
      </div>
      {invalid("status") && <p id={`${id}-status-error`} className="event-field-error">{w("اختر الحالة الجديدة قبل المتابعة.", "Choose the new status to continue.")}</p>}
    </fieldset>
    <div className="event-lifecycle-reason" data-field-path="reason">
      <label htmlFor={`${id}-reason`}>{w("سبب التغيير", "Reason for the change")} <FieldRequirement required={false} /></label>
      <textarea id={`${id}-reason`} rows={3} minLength={schema.properties?.reason.minLength} maxLength={schema.properties?.reason.maxLength} value={String(value.reason ?? "")} aria-invalid={invalid("reason")} aria-describedby={`${id}-reason-help${invalid("reason") ? ` ${id}-reason-error` : ""}`} onChange={event => {
        const next = { ...value };
        if (event.target.value) next.reason = event.target.value; else delete next.reason;
        onChange(next);
      }} />
      <small id={`${id}-reason-help`}>{w("أضف ملاحظة تساعد الفريق على فهم سبب تغيير الحالة.", "Add a note to help the team understand this status change.")}</small>
      {invalid("reason") && <p id={`${id}-reason-error`} className="event-field-error">{w("راجع سبب التغيير قبل الحفظ.", "Review the reason before saving.")}</p>}
    </div>
  </div>;
}
